import { createRoot } from "react-dom/client";

import { config } from "./config";
import "./index.css";
import { installReloadOnPreloadError } from "./utils/reloadOnPreloadError";

installReloadOnPreloadError();

import axios from "axios";
import mixpanel from "mixpanel-browser";
import Sentry from "./integrations/Sentry";
import * as analytics from "./utils/analytics";

import { captureSignupUtm } from "@cohesive/ui-components";
import App from "./App";
import { bootstrapNative } from "./native/bootstrap";

// Native (iOS) runtime setup — no-op on the web. Runs before React mounts so the
// safe-area viewport + root class are in place for the first paint.
bootstrapNative();

// Campaign attribution rides in on the URL the app booted on, whatever the
// route; the Auth0 round trip drops it long before onboarding creates the org.
captureSignupUtm();

const container = document.getElementById("root");

// Unregister any lingering service workers and clear their caches
if ("serviceWorker" in navigator) {
  navigator.serviceWorker.getRegistrations().then((registrations) => {
    for (const registration of registrations) {
      registration.unregister();
    }
  });
  caches.keys().then((keys) => {
    for (const key of keys) {
      caches.delete(key);
    }
  });
}

if (config.github) {
  console.log("App version", config.github);
}

if (!container) {
  throw new Error("React root element doesn't exist!");
}

if (import.meta.env.VITE_DEBUG) {
  localStorage.setItem("debug", import.meta.env.VITE_DEBUG);
}

if (config.mixpanel.token && config.mixpanel.enabled) {
  mixpanel.init(config.mixpanel.token, {
    api_host: config.mixpanel.apiHost,
    debug: config.mixpanel.debug,
    track_pageview: false,
    persistence: "localStorage",
    opt_out_tracking_by_default: !config.mixpanel.enabled,
    record_sessions_percent: 100,
    record_mask_all_text: false,
  });
}

if (config.sentry.enabled) {
  axios.interceptors.response.use(
    (response) => {
      return response;
    },
    (error) => {
      Sentry.captureException(error);
      return Promise.reject(error);
    },
  );
}

window.addEventListener("appinstalled", () => analytics.trackAppInstalled());

const root = createRoot(container!, {
  onUncaughtError: Sentry.reactErrorHandler((error, errorInfo) => {
    console.warn("Uncaught error", error, errorInfo.componentStack);
  }),
  onCaughtError: Sentry.reactErrorHandler(),
  onRecoverableError: Sentry.reactErrorHandler(),
});

root.render(<App />);
